import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { canWrite, canDelete, WRITE_PERMISSION_DENIED, DELETE_PERMISSION_DENIED } from '@/services/permissions';
import { recordAudit } from '@/services/audit';

export interface Size {
  id: string;
  name: string;
  description: string;
  cmRange: string;
  sortOrder: number;
  isActive: boolean;
  // Peso aproximado (kg) usado para el cálculo de flete de Interrapidísimo.
  weightKg: number;
  // Tipo de talla: 'premium' o 'basica'.
  sizeType: string;
}

export interface SizesResult {
  data: Size[];
}

// Consulta las tallas configurables desde `sizes`. Sin datos devuelve un array
// vacío (honesto) en vez de inventar mocks.
export async function fetchSizes(): Promise<SizesResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const { data, error } = await supabase
      .from('sizes')
      .select('*')
      .order('sort_order', { ascending: true });

    if (error) {
      throw new Error(error.message);
    }

    const mapped: Size[] = ((data ?? []) as Array<Record<string, any>>).map((r) => ({
      id: r.id,
      name: r.name,
      description: r.description ?? '',
      cmRange: r.cm_range ?? '',
      sortOrder: Number(r.sort_order ?? 0),
      isActive: r.is_active ?? true,
      weightKg: Number(r.weight_kg ?? 0.5),
      sizeType: r.size_type ?? 'premium',
    }));

    return { data: mapped };
  } catch (err) {
    console.error('[sizes] connection failed:', err);
    throw err;
  }
}

export interface SizeInput {
  name: string;
  description: string;
  cmRange: string;
  sortOrder: number;
  isActive: boolean;
  weightKg: number;
  sizeType: string;
}

// Crea una talla nueva en Supabase.
export async function saveSize(
  input: SizeInput,
): Promise<{ data: Size | null; error: string | null }> {
  if (!(await canWrite('sizes'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const payload = {
    name: input.name.trim(),
    description: input.description.trim() || null,
    cm_range: input.cmRange.trim() || null,
    sort_order: input.sortOrder,
    is_active: input.isActive,
    weight_kg: Number(input.weightKg) || 0,
    size_type: input.sizeType || 'premium',
  };

  try {
    const { data, error } = await supabase.from('sizes').insert(payload).select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as Record<string, any> | undefined;
    if (!row) return { data: null, error: 'No se pudo leer la talla guardada.' };
    recordAudit({
      entityType: 'sizes',
      entityId: row.id,
      action: 'CREATE',
      newValue: input.name,
    });
    return {
      data: {
        id: row.id,
        name: row.name,
        description: row.description ?? '',
        cmRange: row.cm_range ?? '',
        sortOrder: Number(row.sort_order ?? 0),
        isActive: row.is_active ?? true,
        weightKg: Number(row.weight_kg ?? 0),
        sizeType: row.size_type ?? 'premium',
      },
      error: null,
    };
  } catch (err) {
    console.warn('[sizes] save failed:', err);
    return { data: null, error: 'Error de conexión al guardar la talla.' };
  }
}

// Alterna el estado activo de una talla (activar/desactivar).
export async function toggleSize(
  id: string,
  isActive: boolean,
): Promise<{ error: string | null }> {
  if (!(await canWrite('sizes'))) {
    return { error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { error: 'Supabase no está configurado.' };
  }

  try {
    const { error } = await supabase.from('sizes').update({ is_active: isActive }).eq('id', id);
    if (!error) {
      recordAudit({
        entityType: 'sizes',
        entityId: id,
        action: 'UPDATE',
        fieldName: 'is_active',
        oldValue: String(!isActive),
        newValue: String(isActive),
      });
    }
    return { error: error?.message ?? null };
  } catch (err) {
    console.warn('[sizes] toggle failed:', err);
    return { error: 'Error de conexión al actualizar la talla.' };
  }
}

// Actualiza una talla existente (nombre, descripción, orden y estado).
export async function updateSize(
  id: string,
  input: SizeInput,
): Promise<{ data: Size | null; error: string | null }> {
  if (!(await canWrite('sizes'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const payload = {
    name: input.name.trim(),
    description: input.description.trim() || null,
    cm_range: input.cmRange.trim() || null,
    sort_order: input.sortOrder,
    is_active: input.isActive,
    weight_kg: Number(input.weightKg) || 0,
    size_type: input.sizeType || 'premium',
  };

  try {
    const { data, error } = await supabase
      .from('sizes')
      .update(payload)
      .eq('id', id)
      .select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as Record<string, any> | undefined;
    if (!row) return { data: null, error: 'No se pudo leer la talla actualizada.' };
    recordAudit({
      entityType: 'sizes',
      entityId: id,
      action: 'UPDATE',
      fieldName: 'name',
      newValue: input.name,
    });
    return {
      data: {
        id: row.id,
        name: row.name,
        description: row.description ?? '',
        cmRange: row.cm_range ?? '',
        sortOrder: Number(row.sort_order ?? 0),
        isActive: row.is_active ?? true,
        weightKg: Number(row.weight_kg ?? 0),
        sizeType: row.size_type ?? 'premium',
      },
      error: null,
    };
  } catch (err) {
    console.warn('[sizes] update failed:', err);
    return { data: null, error: 'Error de conexión al actualizar la talla.' };
  }
}

// Elimina una talla. Si está referenciada por variantes de producto, la base
// de datos rechaza la operación y se devuelve un mensaje amigable.
export async function deleteSize(id: string): Promise<{ error: string | null }> {
  if (!(await canDelete('sizes'))) {
    return { error: DELETE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { error: 'Supabase no está configurado.' };
  }

  try {
    const { error } = await supabase.from('sizes').delete().eq('id', id);
    if (error) {
      if (error.code === '23503') {
        return {
          error:
            'No se puede eliminar esta talla porque está en uso por productos o inventario. Desactívala si no quieres que aparezca.',
        };
      }
      return { error: error.message };
    }
    recordAudit({
      entityType: 'sizes',
      entityId: id,
      action: 'DELETE',
    });
    return { error: null };
  } catch (err) {
    console.warn('[sizes] delete failed:', err);
    return { error: 'Error de conexión al eliminar la talla.' };
  }
}